export const DEFAULT_DOLLAR_BIN_SIZE = 500
export const DEFAULT_TARGET_PCT = 0.006
export const FALLBACK_MARKET_PRICE = 70000

function getPriceBin(price, binSize) {
  return Math.floor(price / binSize) * binSize
}

function emptyBin(binFloor, step) {
  return {
    binFloor,
    priceMin: binFloor,
    priceMax: binFloor + step,
    longQty: 0,
    shortQty: 0,
    longCostVal: 0,
    shortVal: 0,
  }
}

export function resolveStep({
  isTargetMode = false,
  marketPrice = 0,
  targetPct = DEFAULT_TARGET_PCT,
  dollarBinSize = DEFAULT_DOLLAR_BIN_SIZE,
} = {}) {
  if (!isTargetMode) {
    return dollarBinSize > 0 ? dollarBinSize : DEFAULT_DOLLAR_BIN_SIZE
  }
  const price = marketPrice > 0 ? marketPrice : FALLBACK_MARKET_PRICE
  const pct = targetPct > 0 ? targetPct : DEFAULT_TARGET_PCT
  return price * pct
}

/**
 * Agrupa o estoque ativo em faixas de densidade (IDI).
 * Entrada já é um snapshot neutro — sem Vue/Vuex.
 *
 * @param {object} input
 * @param {Array<{price: number, qty: number}>} input.longs
 * @param {Array<{price: number, qty: number}>} input.shorts
 * @param {number} input.marketPrice
 * @param {boolean} input.isTargetMode
 * @param {number} input.targetPct  fração (ex.: 0.006)
 * @param {number} input.dollarBinSize
 */
export function computeDensityBins({
  longs = [],
  shorts = [],
  marketPrice = 0,
  isTargetMode = false,
  targetPct = DEFAULT_TARGET_PCT,
  dollarBinSize = DEFAULT_DOLLAR_BIN_SIZE,
} = {}) {
  const step = resolveStep({ isTargetMode, marketPrice, targetPct, dollarBinSize })
  if (!step || step <= 0) return []

  const pct = targetPct > 0 ? targetPct : DEFAULT_TARGET_PCT
  const binsMap = new Map()

  function ensureBin(price) {
    const binFloor = getPriceBin(price, step)
    if (!binsMap.has(binFloor)) {
      binsMap.set(binFloor, emptyBin(binFloor, step))
    }
    return binsMap.get(binFloor)
  }

  longs.forEach((lot) => {
    const origPrice = Number(lot.price || 0)
    const qty = Number(lot.qty || 0)
    if (origPrice <= 0 || qty <= 0) return
    const mappedPrice = isTargetMode ? origPrice * (1 + pct) : origPrice
    const bin = ensureBin(mappedPrice)
    bin.longQty += qty
    bin.longCostVal += origPrice * qty
  })

  shorts.forEach((lot) => {
    const origPrice = Number(lot.price || 0)
    const qty = Number(lot.qty || 0)
    if (origPrice <= 0 || qty <= 0) return
    const mappedPrice = isTargetMode ? origPrice * (1 - pct) : origPrice
    const bin = ensureBin(mappedPrice)
    bin.shortQty += qty
    bin.shortVal += origPrice * qty
  })

  if (marketPrice > 0) {
    ensureBin(marketPrice)
  }

  return Array.from(binsMap.values())
    .map((bin) => {
      let longQty = bin.longQty
      let shortQty = bin.shortQty
      let longCostVal = bin.longCostVal
      let shortVal = bin.shortVal

      const isCurrentRange = marketPrice >= bin.priceMin && marketPrice < bin.priceMax

      if (isTargetMode) {
        if (bin.priceMin > marketPrice) {
          shortQty = 0
          shortVal = 0
        } else if (bin.priceMax < marketPrice) {
          longQty = 0
          longCostVal = 0
        }
      }

      const netDelta = longQty - shortQty
      const avgLongPrice = longQty > 0 ? longCostVal / longQty : 0
      const avgShortPrice = shortQty > 0 ? shortVal / shortQty : 0
      const binMidPrice = (bin.priceMin + bin.priceMax) / 2
      const evalPrice = isTargetMode ? binMidPrice : marketPrice
      const spotPnl = evalPrice > 0 && longQty > 0 ? (evalPrice - avgLongPrice) * longQty : 0
      const shortPnl = evalPrice > 0 && shortQty > 0 ? (avgShortPrice - evalPrice) * shortQty : 0

      return {
        binFloor: bin.binFloor,
        priceMin: bin.priceMin,
        priceMax: bin.priceMax,
        longQty,
        shortQty,
        longCostVal,
        shortVal,
        netDelta,
        isCurrentRange,
        avgLongPrice,
        avgShortPrice,
        spotPnl,
        shortPnl,
      }
    })
    .filter((bin) => bin.longQty > 0 || bin.shortQty > 0 || bin.isCurrentRange)
    .sort((a, b) => b.priceMin - a.priceMin)
}

export function calculateRcrLocal(shorts = [], marketPrice = 0, proposedLongQty = 0) {
  if (!proposedLongQty || proposedLongQty <= 0) return 0
  if (!marketPrice || marketPrice <= 0) return 0

  const shortQtyAboveCurr = shorts
    .filter((lot) => Number(lot.price || 0) >= marketPrice)
    .reduce((sum, lot) => sum + Number(lot.qty || 0), 0)

  return shortQtyAboveCurr / proposedLongQty
}
